import './profile-menu.scss';

import { Avatar, Button, Dropdown, type MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

import { ADMIN_URL } from '@/api/config';
import {
  DownOutlined,
  GlobalOutlined,
  LogoutOutlined,
  MoonOutlined,
  SettingsOutlined,
  SunOutlined
} from '@/media/antd-icons';
import {
  LANG_NATIVE_NAMES,
  type Languages,
  SUPPORTED_LANGUAGES
} from '@/shared/constants';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import useThemeToggle from '@/shared/hooks/useThemeToggle';
import { getInitials } from '@/shared/services/helpers/strings';
import { useAppSelector } from '@/store';

import useLogout from './hooks/useLogout';

const LANGUAGE_KEY_PREFIX = 'lang:';

/**
 * Аватар и имя сотрудника. В меню — кто вошёл, язык, тема, админ-панель
 * (для администратора) и выход.
 */
const ProfileMenu: React.FC = () => {
  const { t } = useTranslation();
  const logout = useLogout();
  const { changeLanguage, currentLang } = useAppNavigate();
  const { isDark, toggleTheme } = useThemeToggle();
  const { employee, accesses } = useAppSelector((state) => state.session);

  const fullName = employee ? `${employee.firstName} ${employee.lastName}` : '';

  const items: MenuProps['items'] = [
    ...(employee
      ? [
          {
            key: 'employee',
            type: 'group' as const,
            label: (
              <span className="profile-menu__identity">
                <span className="profile-menu__name">{fullName}</span>
                {employee.position && <span>{employee.position.name}</span>}
              </span>
            )
          },
          { type: 'divider' as const }
        ]
      : []),
    {
      key: 'language',
      icon: <GlobalOutlined />,
      className: 'profile-menu__language',
      label: `${t('layout.profile.language')}: ${LANG_NATIVE_NAMES[currentLang as Languages]}`,
      children: SUPPORTED_LANGUAGES.map((lang) => ({
        key: `${LANGUAGE_KEY_PREFIX}${lang}`,
        label: LANG_NATIVE_NAMES[lang],
        onClick: () => changeLanguage(lang)
      }))
    },
    {
      key: 'theme',
      icon: isDark ? <SunOutlined /> : <MoonOutlined />,
      label: t(isDark ? 'layout.theme.toLight' : 'layout.theme.toDark'),
      onClick: toggleTheme
    },
    ...(accesses.global.isAdmin && ADMIN_URL
      ? [
          {
            key: 'admin',
            icon: <SettingsOutlined />,
            label: <a href={ADMIN_URL}>{t('layout.profile.adminPanel')}</a>
          }
        ]
      : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      label: t('layout.profile.logout'),
      icon: <LogoutOutlined />,
      onClick: logout
    }
  ];

  return (
    <Dropdown
      menu={{
        items,
        selectedKeys: [`${LANGUAGE_KEY_PREFIX}${currentLang}`]
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button
        type="text"
        size="large"
        className="profile-menu"
        aria-label={t('layout.profile.label')}
      >
        <Avatar className="profile-menu__avatar">
          {getInitials(fullName)}
        </Avatar>
        <span className="profile-menu__trigger-name">{fullName}</span>
        <DownOutlined className="profile-menu__arrow" />
      </Button>
    </Dropdown>
  );
};

export default ProfileMenu;
